import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { updateProjectStatus } from "store/customerProject/reducer";
import { uploadProjectSelector } from "store/customerProject/selector";
import {
  calculateOrderTotal,
  calculateStandardAddonCost,
  calculateStandardTranslationCost,
  fetchPerPagePrice,
  MAILED_HARD_COPY_USD,
  NOTARIZATION_USD,
  STANDARD_DESKTOP_PUB_PER_WORD_USD,
  STANDARD_EXPEDITED_PER_WORD_USD,
  STANDARD_PROOFREADING_PER_WORD_USD,
} from "../constants/pricing";
import { WizardStep } from "../types";

const LS_MAX_STEP = "order.maxStep";

export default function OrderSummary(props: {
  step?: WizardStep;
  maxStep?: WizardStep;
}) {
  const { step, maxStep } = props;
  const dispatch = useDispatch();
  const uploadProject = useSelector(uploadProjectSelector);

  const persistedMaxStep: WizardStep | undefined = (() => {
    try {
      const raw = Number(localStorage.getItem(LS_MAX_STEP));
      if (raw === 1 || raw === 2 || raw === 3 || raw === 4)
        return raw as WizardStep;
      return undefined;
    } catch (e) {
      return undefined;
    }
  })();

  const effectiveStep = (maxStep ||
    persistedMaxStep ||
    step ||
    1) as WizardStep;

  // True if any uploaded document came back from the backend with
  // parserSuccess: false (parser threw OR returned the suspicious (1,1)
  // fallback). When set, we render a manual-review banner so the customer
  // understands the displayed total excludes the unparsed file(s).
  const hasUnparsedDocument = useMemo(() => {
    const docs = (uploadProject.documents || []) as any[];
    return docs.some((d: any) => d?.parserSuccess === false);
  }, [uploadProject.documents]);

  const summary = useMemo(() => {
    const perPage = fetchPerPagePrice(uploadProject.targetLanguage || []);
    const words = Math.max(0, Number(uploadProject.totalWords || 0) || 0);

    // Base translation cost. Certified is per-page; Standard uses the
    // shared $0.10/word + $25-floor helper (PROD-2026-05-18-002 / -003)
    // so the displayed base never drifts from calculateOrderTotal.
    const base = (() => {
      if (uploadProject.transitionType === "certified") {
        return (Number(uploadProject.totalPage || 0) || 0) * perPage;
      }
      return calculateStandardTranslationCost(words);
    })();

    const total = calculateOrderTotal({
      transitionType: uploadProject.transitionType,
      include_expedited_turnaround: (uploadProject as any)
        .include_expedited_turnaround,
      totalPage: uploadProject.totalPage,
      totalWords: uploadProject.totalWords,
      targetLanguage: uploadProject.targetLanguage || [],
      isNotarized: uploadProject.isNotarized,
      isMailHardCopy: uploadProject.isMailHardCopy,
      include_desktop_publishing: (uploadProject as any)
        .include_desktop_publishing,
      include_proofreading: (uploadProject as any).include_proofreading,
    });

    const notarization = uploadProject.isNotarized ? NOTARIZATION_USD : 0;
    const mailedHardCopy = uploadProject.isMailHardCopy ? MAILED_HARD_COPY_USD : 0;
    const includeExpedited =
      (uploadProject as any).include_expedited_turnaround === true ||
      (uploadProject as any).include_expedited_turnaround === 1 ||
      (uploadProject as any).include_expedited_turnaround === "1";

    const pagesStandard = Math.max(
      0,
      Number(uploadProject.totalPage || 0) || 0,
    );

    // Standard add-ons: per-word with $10 floor — same helpers used in
    // calculateOrderTotal so each row sums to the same checkout total.
    // Certified expedited stays per-page (tier convention).
    const isCertified = uploadProject.transitionType === "certified";
    const expeditedTurnaround = includeExpedited
      ? isCertified
        ? 10 * pagesStandard
        : calculateStandardAddonCost(STANDARD_EXPEDITED_PER_WORD_USD, words)
      : 0;

    const desktopPublishing = (uploadProject as any).include_desktop_publishing
      ? calculateStandardAddonCost(STANDARD_DESKTOP_PUB_PER_WORD_USD, words)
      : 0;
    const proofreading = (uploadProject as any).include_proofreading
      ? calculateStandardAddonCost(STANDARD_PROOFREADING_PER_WORD_USD, words)
      : 0;

    const addOnsTotal = isCertified
      ? notarization + mailedHardCopy + expeditedTurnaround
      : expeditedTurnaround + desktopPublishing + proofreading;

    return {
      perPage,
      base,
      total,
      pagesStandard,
      addOnsTotal,
      notarization,
      mailedHardCopy,
      expeditedTurnaround,
      desktopPublishing,
      proofreading,
      includeExpedited,
    };
  }, [uploadProject]);

  const displayedTotal = useMemo(() => {
    const serverTotal = Number((uploadProject as any)?.serverTotal || 0) || 0;
    if (serverTotal > 0) return serverTotal;
    return summary.total;
  }, [summary.total, uploadProject]);

  const showLanguageLine =
    effectiveStep >= 2 && !!uploadProject.targetLanguage?.[0];
  const showAddOns = effectiveStep >= 3;
  const showDeliveryDate = effectiveStep >= 4;

  const fromLang = uploadProject.sourceLanguage || "English";
  const toLang = uploadProject.targetLanguage?.[0];
  const languagePairLabel = toLang ? `${fromLang} to ${toLang}` : "";

  const pagesOrWordsLabel =
    uploadProject.transitionType === "certified"
      ? `${Number(uploadProject.totalPage || 0) || 0} pages`
      : `${Number(uploadProject.totalWords || 0) || 0} words`;

  const deliveryDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  return (
    <Box
      width="100%"
      maxW={{ base: "100%", md: "371px" }}
      mr={{ base: "0", md: "auto" }}
      maxH={{ base: "none", md: "371px" }}
      position={{ base: "static", md: "sticky" }}
      top={{ base: "auto", md: "16px" }}
    >
      <Box
        p={{ base: "24px", md: "16px" }}
        maxH={{ base: "none", md: "100vh" }}
        maxW={{ base: "100%", md: "371px" }}
        borderRadius="12px"
        bg="#F5F7FA"
        border={{ base: "1px solid #E6ECF3", md: "none" }}
      >
        <Heading fontSize="24px" fontWeight="600" color="#0B0E10" mb="24px">
          Order Summary
        </Heading>

        {hasUnparsedDocument && (
          <Box
            mb="16px"
            p="12px"
            borderRadius="8px"
            bg="#FFF8E1"
            border="1px solid #F2C94C"
            role="status"
            aria-live="polite"
          >
            <Text fontSize="13px" fontWeight="600" color="#7A5B00" mb="2px">
              Manual review required
            </Text>
            <Text fontSize="13px" fontWeight="400" color="#7A5B00" lineHeight="1.4">
              We couldn&apos;t auto-count one or more of your files. Enter an
              estimate next to the file, or request a free custom quote — a
              Languex specialist verifies every estimate before your card is
              charged.
            </Text>
          </Box>
        )}

        <Box>
          <Text fontSize="16px" fontWeight="600" color="#0B0E10">
            {uploadProject.transitionType === "certified"
              ? "Certified translation"
              : "Standard translation"}
          </Text>
          {uploadProject.transitionType === "certified" ? (
            <>
              <Text mt="6px" fontSize="16px" fontWeight="500" color="#0B0E10">
                Charged per page: ${summary.perPage.toFixed(2)}
              </Text>
              <Text fontSize="16px" fontWeight="500" color="#0B0E10">
                (250 max words per page)
              </Text>
            </>
          ) : (
            <>
              <Text mt="6px" fontSize="16px" fontWeight="500" color="#0B0E10">
                Charged per word: $0.10
              </Text>
              <Text fontSize="16px" fontWeight="500" color="#0B0E10">
                $25.00 minimum (1 page = 250 words)
              </Text>
            </>
          )}
        </Box>

        {showLanguageLine ? (
          <>
            <Flex
              mt="24px"
              justifyContent="space-between"
              alignItems="flex-start"
              gap="12px"
            >
              <Box minW="0">
                <Text
                  fontSize="16px"
                  fontWeight="700"
                  color="#0B0E10"
                  noOfLines={1}
                >
                  {languagePairLabel}
                </Text>
                <Text mt="4px" fontSize="16px" color="#0B0E10">
                  {pagesOrWordsLabel}
                </Text>
              </Box>
              <Text
                fontSize="16px"
                fontWeight="700"
                color="#0B0E10"
                whiteSpace="nowrap"
              >
                ${summary.base.toFixed(2)}
              </Text>
            </Flex>
          </>
        ) : null}

        {showAddOns ? (
          <>
            <Box mt="24px" borderTop="1px solid #D9D9D9" />
            <Flex mt="16px" justifyContent="space-between" alignItems="center">
              <Flex alignItems="center" gap="16px" minW="0">
                <Text fontSize="16px" fontWeight="700" color="#0B0E10">
                  Add-ons
                </Text>
                <Box
                  px="10px"
                  py="3px"
                  borderRadius="4px"
                  bg="#D6EFE1"
                  color="#2B9057"
                  fontSize="12px"
                  fontWeight="600"
                >
                  RECOMMENDED
                </Box>
              </Flex>
              <Text
                fontSize="16px"
                fontWeight="700"
                color="#0B0E10"
                whiteSpace="nowrap"
              >
                ${summary.addOnsTotal.toFixed(2)}
              </Text>
            </Flex>

            <Box mt="12px">
              {uploadProject.transitionType === "certified" ? (
                <>
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    gap="8px"
                    cursor={uploadProject.isNotarized ? "default" : "pointer"}
                    onClick={() => {
                      if (uploadProject.isNotarized) return;
                      dispatch(updateProjectStatus({ isNotarized: true }));
                    }}
                  >
                    <Text fontSize="16px" color="#0B0E10">
                      Notarization
                    </Text>
                    <Text fontSize="16px" color="#0B0E10" whiteSpace="nowrap">
                      {uploadProject.isNotarized ? (
                        `$${NOTARIZATION_USD.toFixed(2)}`
                      ) : (
                        <Text
                          fontSize="14px"
                          as="span"
                          color="#3A7BFF"
                          fontWeight="700"
                          textDecoration="underline"
                          cursor="pointer"
                        >
                          Add
                        </Text>
                      )}
                    </Text>
                  </Flex>

                  <Flex
                    mt="6px"
                    justifyContent="space-between"
                    alignItems="center"
                    gap="12px"
                    cursor={
                      summary.includeExpedited
                        ? "default"
                        : "pointer"
                    }
                    onClick={() => {
                      if (summary.includeExpedited) return;
                      dispatch(
                        updateProjectStatus({
                          include_expedited_turnaround: true,
                        }),
                      );
                    }}
                  >
                    <Text fontSize="16px" color="#0B0E10">
                      Expedited turnaround
                    </Text>
                    <Text fontSize="16px" color="#0B0E10" whiteSpace="nowrap">
                      {summary.includeExpedited ? (
                        `$${summary.expeditedTurnaround.toFixed(2)}`
                      ) : (
                        <Text
                          fontSize="14px"
                          as="span"
                          color="#3A7BFF"
                          fontWeight="700"
                          textDecoration="underline"
                          cursor="pointer"
                        >
                          Add
                        </Text>
                      )}
                    </Text>
                  </Flex>

                  <Flex
                    mt="6px"
                    justifyContent="space-between"
                    alignItems="center"
                    gap="12px"
                    cursor={
                      uploadProject.isMailHardCopy ? "default" : "pointer"
                    }
                    onClick={() => {
                      if (uploadProject.isMailHardCopy) return;
                      dispatch(updateProjectStatus({ isMailHardCopy: true }));
                    }}
                  >
                    <Text fontSize="16px" color="#0B0E10">
                      Mailed hard copy
                    </Text>
                    <Text fontSize="16px" color="#0B0E10" whiteSpace="nowrap">
                      {uploadProject.isMailHardCopy ? (
                        `$${MAILED_HARD_COPY_USD.toFixed(2)}`
                      ) : (
                        <Text
                          fontSize="14px"
                          as="span"
                          color="#3A7BFF"
                          fontWeight="700"
                          textDecoration="underline"
                          cursor="pointer"
                        >
                          Add
                        </Text>
                      )}
                    </Text>
                  </Flex>
                </>
              ) : (
                <>
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    gap="8px"
                    cursor={
                      summary.includeExpedited
                        ? "default"
                        : "pointer"
                    }
                    onClick={() => {
                      if (summary.includeExpedited) return;
                      dispatch(
                        updateProjectStatus({
                          include_expedited_turnaround: true,
                        }),
                      );
                    }}
                  >
                    <Text fontSize="16px" color="#0B0E10">
                      Expedited turnaround
                    </Text>
                    <Text fontSize="16px" color="#0B0E10" whiteSpace="nowrap">
                      {summary.includeExpedited ? (
                        `$${summary.expeditedTurnaround.toFixed(2)}`
                      ) : (
                        <Text
                          fontSize="14px"
                          as="span"
                          color="#3A7BFF"
                          fontWeight="700"
                          textDecoration="underline"
                          cursor="pointer"
                        >
                          Add
                        </Text>
                      )}
                    </Text>
                  </Flex>

                  <Flex
                    mt="6px"
                    justifyContent="space-between"
                    alignItems="center"
                    gap="12px"
                    cursor={
                      (uploadProject as any).include_desktop_publishing
                        ? "default"
                        : "pointer"
                    }
                    onClick={() => {
                      if ((uploadProject as any).include_desktop_publishing)
                        return;
                      dispatch(
                        updateProjectStatus({
                          include_desktop_publishing: true,
                        }),
                      );
                    }}
                  >
                    <Text fontSize="16px" color="#0B0E10">
                      Desktop Publishing
                    </Text>
                    <Text fontSize="16px" color="#0B0E10" whiteSpace="nowrap">
                      {(uploadProject as any).include_desktop_publishing ? (
                        `$${summary.desktopPublishing.toFixed(2)}`
                      ) : (
                        <Text
                          fontSize="14px"
                          as="span"
                          color="#3A7BFF"
                          fontWeight="700"
                          textDecoration="underline"
                          cursor="pointer"
                        >
                          Add
                        </Text>
                      )}
                    </Text>
                  </Flex>

                  <Flex
                    mt="6px"
                    justifyContent="space-between"
                    alignItems="center"
                    gap="12px"
                    cursor={
                      (uploadProject as any).include_proofreading
                        ? "default"
                        : "pointer"
                    }
                    onClick={() => {
                      if ((uploadProject as any).include_proofreading) return;
                      dispatch(
                        updateProjectStatus({ include_proofreading: true }),
                      );
                    }}
                  >
                    <Text fontSize="16px" color="#0B0E10">
                      Proofreading
                    </Text>
                    <Text fontSize="16px" color="#0B0E10" whiteSpace="nowrap">
                      {(uploadProject as any).include_proofreading ? (
                        `$${summary.proofreading.toFixed(2)}`
                      ) : (
                        <Text
                          fontSize="14px"
                          as="span"
                          color="#3A7BFF"
                          fontWeight="700"
                          textDecoration="underline"
                          cursor="pointer"
                        >
                          Add
                        </Text>
                      )}
                    </Text>
                  </Flex>
                </>
              )}
            </Box>
          </>
        ) : null}

        <Box mt="24px" borderTop="1px solid #D9D9D9" />

        <Flex mt="20px" justifyContent="space-between" alignItems="center">
          <Text fontSize="18px" fontWeight="600" color="#0B0E10">
            Order total
          </Text>
          <Text fontSize="18px" fontWeight="600" color="#0B0E10">
            ${displayedTotal.toFixed(2)}
          </Text>
        </Flex>

        {showDeliveryDate ? (
          <>
            <Box mt="24px" borderTop="1px solid #D9D9D9" />
            <Box mt="24px">
              <Text fontSize="16px" fontWeight="700" color="#0B0E10">
                Delivery date
              </Text>
              <Text mt="8px" fontSize="16px" color="#0B0E10">
                Receive your translations by:{" "}
                <Text as="span" fontWeight="700" color="#37474F">
                  {deliveryDate}
                </Text>
              </Text>
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
